import "./employment-contract.scss";

import { useState } from "react";

import { useMutation } from "@apollo/client/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { saveAs } from "file-saver";
import { Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { MdFileDownload } from "react-icons/md";
import { useParams } from "react-router-dom";
import * as z from "zod/v4";

import { personalFileDownloadPath } from "@path/staff_paths";

import FlashMessagesList from "@/components/flash";
import { addDangerFlash } from "@/components/flash/flashReducer";
import { DoneButton } from "@/containers/buttons";
import FormActionsContainer from "@/containers/FormActionsContainer";
import NoLoginPageHeader from "@/containers/NoLoginPageHeader";
import RHInput from "@/containers/RHInput";
import Spinner from "@/containers/Spinner";
import { SendPersonalFileChallengeDocument, VerifyPersonalFileChallengeDocument } from "@/gql/graphql";
import handleError from "@/handleError";
import { useAppDispatch } from "@/hooks";
import useTitle from "@/hooks/useTitle";

const validationSchema = z.object({
  code: z.string().trim().nonempty("Bitte geben Sie den Code an!"),
});

type ValuesType = z.infer<typeof validationSchema>;

const INITIAL_VALUES: ValuesType = { code: "" };

export default function EmploymentContract() {
  const [hasRequestedChallenge, setHasRequestedChallenge] = useState(false);
  const dispatch = useAppDispatch();
  const { id } = useParams<"id">();
  const [sendPersonalFileChallenge] = useMutation(SendPersonalFileChallengeDocument);
  const [verifyPersonalFileChallenge] = useMutation(VerifyPersonalFileChallengeDocument);

  useTitle("Arbeitsvertrag herunterladen");

  async function requestChallenge() {
    try {
      const { data } = await sendPersonalFileChallenge({ variables: { id: id } });

      if (!data?.sendPersonalFileChallenge) {
        dispatch(addDangerFlash("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
      }

      setHasRequestedChallenge(true);
    } catch (e) {
      dispatch(addDangerFlash("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
      handleError(e);
    }
  }

  async function verifyPersonalFileChallengeAndDownload(values: ValuesType) {
    const { data } = await verifyPersonalFileChallenge({
      variables: {
        id: id,
        response: values.code.toString(),
      },
    });

    if (!data?.verifyPersonalFileChallenge) {
      dispatch(addDangerFlash("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
    } else {
      const rsp = await fetch(personalFileDownloadPath({ id } as any), {
        method: "get",
        cache: "no-cache",
        credentials: "same-origin",
        referrer: "no-referrer",
        headers: {
          Authorization: `Bearer ${data.verifyPersonalFileChallenge}`,
        },
      });

      const blob = await rsp.blob();
      saveAs(blob, "vertrag-termitel.pdf");
    }
  }

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ValuesType>({
    resolver: zodResolver(validationSchema),
    defaultValues: INITIAL_VALUES,
    mode: "onBlur",
  });

  return (
    <div className="TT-staff-emplyee-contract">
      <NoLoginPageHeader />

      <div className="main">
        <h1>Arbeitsvertrag herunterladen</h1>

        <FlashMessagesList inline />

        <p>
          Um Ihre Identität zu bestätigen, werden wir ihnen eine SMS mit einer 6-stelligen Nummer zusenden. Bitte geben
          Sie die hier ein.
        </p>

        <p>
          <DoneButton disabled={hasRequestedChallenge} onClick={requestChallenge}>
            Code anfordern
          </DoneButton>
        </p>

        <Form onSubmit={handleSubmit(verifyPersonalFileChallengeAndDownload)}>
          <RHInput
            control={control}
            id="code"
            name="code"
            type="number"
            min={0}
            step={1}
            label="Authentifizierungs-Code"
          />

          <FormActionsContainer>
            <Button variant="secondary" disabled={isSubmitting} type="submit">
              {isSubmitting ? <Spinner /> : <MdFileDownload />} Code absenden und Datei herunterladen
            </Button>
          </FormActionsContainer>
        </Form>
      </div>
    </div>
  );
}
